import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ConfirmationDialog } from "ui/confirmation-dialog";
import { TextField } from "ui/text-field";
import toast from "components/partials/toast/toast";
import CollegeProfileBasicInformationSection from "containers/admin/clients/client/college-profile-page/college-profile-basic-information-section";
import CollegeProfileEmailTypesSection from "containers/admin/clients/client/college-profile-page/college-profile-email-types-section";
import CollegeProfileGeneralDetailsSection from "containers/admin/clients/client/college-profile-page/college-profile-general-details-section";
import CollegeProfileNav from "containers/admin/clients/client/college-profile-page/college-profile-nav";
import { DEFAULT_COLLEGE_PROFILE_SECTION_ID, type CollegeProfileSectionId } from "containers/admin/clients/client/college-profile-page/college-profile-page.types";
import CollegeProfileParentEmailRulesSection, {
  PARENT_EMAIL_RULES_VIEW,
} from "containers/admin/clients/client/college-profile-page/college-profile-parent-email-rules-section";

import { useUnsavedChangesNavigation } from "contexts/unsaved-changes-navigation-context";
import { useCurrentClient, useDeleteClient } from "hooks/api/use-clients";

import { getErrorMessage } from "utilities";

const CollegeProfilePage: React.FC = () => {
  const client = useCurrentClient();
  const navigate = useNavigate();
  const deleteClientMutation = useDeleteClient();
  const [activeSectionId, setActiveSectionId] = useState<CollegeProfileSectionId>(
    DEFAULT_COLLEGE_PROFILE_SECTION_ID,
  );
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [isEditingGeneralDetails, setIsEditingGeneralDetails] = useState(false);
  const [isEditingBasicInformation, setIsEditingBasicInformation] = useState(false);
  const { requestNavigation } = useUnsavedChangesNavigation();

  const switchSection = useCallback((sectionId: CollegeProfileSectionId) => {
    setActiveSectionId(sectionId);
    setIsEditingGeneralDetails(false);
    setIsEditingBasicInformation(false);
  }, []);

  const handleSectionSelect = useCallback(
    (sectionId: CollegeProfileSectionId) => {
      // Switching sections is in-page state, not a route change, but every section registers
      // itself with the same app-wide unsaved-changes guard while it's being edited and dirty
      // (see `hooks/use-unsaved-changes-guard.tsx`) — routing the switch through
      // `requestNavigation` lets whichever section is active intercept it exactly like it would
      // a real navigation attempt, no extra page-level bookkeeping needed.
      requestNavigation({ run: () => switchSection(sectionId) });
    },
    [requestNavigation, switchSection],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setTypedName("");
    setIsDeleteOpen(false);
  }, []);

  const handleOpenDeleteModal = useCallback(() => {
    setTypedName("");
    setIsDeleteOpen(true);
  }, []);

  const handleTypedNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTypedName(event.target.value);
  }, []);

  const handleDeleteClient = useCallback(() => {
    deleteClientMutation
      .mutateAsync({ id: client.id })
      .then(() => {
        toast.success({
          data: {
            title: `Deleted ${client.name}`,
          },
        });
        handleCloseDeleteModal();
        navigate("/clients");
      })
      .catch((err) => {
        toast.error({
          data: {
            title: `Failed to delete ${client.name}`,
            message: getErrorMessage(err?.response?.data),
          },
        });
      });
  }, [client.id, client.name, deleteClientMutation, handleCloseDeleteModal, navigate]);

  const renderActiveSection = () => {
    switch (activeSectionId) {
      case "general-details":
        return (
          <CollegeProfileGeneralDetailsSection
            isEditing={isEditingGeneralDetails}
            onEditingChange={setIsEditingGeneralDetails}
          />
        );
      case "basic-information":
        return (
          <CollegeProfileBasicInformationSection
            isEditing={isEditingBasicInformation}
            onEditingChange={setIsEditingBasicInformation}
            onOpenDeleteClientModal={handleOpenDeleteModal}
          />
        );
      case "email-types":
        return <CollegeProfileEmailTypesSection clientId={client.id} />;
      case "parent-email-rules-client-rules":
        return (
          <CollegeProfileParentEmailRulesSection
            clientId={client.id}
            view={PARENT_EMAIL_RULES_VIEW.CLIENT}
          />
        );
      case "parent-email-rules-system-rules":
        return (
          <CollegeProfileParentEmailRulesSection
            clientId={client.id}
            view={PARENT_EMAIL_RULES_VIEW.SYSTEM}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex flex-col flex-1 overflow-hidden bg-gray-100 pb-5 pl-3 pr-5 pt-3 gap-3">
        <div className="flex flex-1 overflow-hidden bg-white border border-gray-200 rounded-lg">
          <CollegeProfileNav activeSectionId={activeSectionId} onSectionSelect={handleSectionSelect} />
          <article className="flex-1 overflow-y-auto flex flex-col bg-white p-4 gap-2">
            {renderActiveSection()}
          </article>
        </div>
      </div>
      {isDeleteOpen && (
        <ConfirmationDialog
          open
          intent="warning"
          headline="Are you sure you want to delete this client?"
          message={
            <>
              This will remove this client from Apollo permanently. This action cannot be undone.
              This will not automatically remove the client&apos;s data from related systems, and
              request should be made to{" "}
              <Link
                className="text-aqua-700 underline"
                to="#"
                onClick={(event) => {
                  event.preventDefault();
                  window.location.href =
                    "mailto:support@marketviewedu.com?subject=Remove all client's data";
                }}
              >
                support@marketviewedu.com
              </Link>{" "}
              to finish off-boarding client.
            </>
          }
          confirmButtonText="Delete client"
          cancelButtonText="Cancel"
          disabled={typedName !== client.name}
          confirming={deleteClientMutation.isPending}
          onClose={handleCloseDeleteModal}
          onConfirm={handleDeleteClient}
        >
          <div>
            <TextField
              name="confirm-client-name"
              label="Client name"
              required
              value={typedName}
              onChange={handleTypedNameChange}
              data-testid="college-profile-delete-client-name-input"
            />
          </div>
        </ConfirmationDialog>
      )}
    </>
  );
};

export default CollegeProfilePage;
